import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Store, Upload, CheckCircle2, Clock, Camera, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";

export default function SellerApply() {
  const { user, refresh } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    business_name: user?.business_name || "", full_name: user?.full_name || user?.name || "", phone: user?.phone || "",
    business_address: user?.business_address || "", country: user?.country || "", tax_number: user?.tax_number || "",
    bank_details: user?.bank_details || "", mobile_money: user?.mobile_money || "",
  });
  const [docs, setDocs] = useState({ gov_id_doc: "", bank_statement_doc: "", selfie_doc: "", business_cert_doc: "", export_license_doc: "" });
  const [loading, setLoading] = useState(false);
  const [uploadingKey, setUploadingKey] = useState("");

  // Base64 for optional legacy docs
  const upload = (key) => (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => { setDocs((d) => ({ ...d, [key]: reader.result })); toast.success(`${file.name} attached`); };
    reader.readAsDataURL(file);
  };

  // Secure private upload for verification docs (bank statement, ID, selfie)
  const uploadSecure = (kind, key) => async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingKey(key);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/seller/verification/upload?kind=${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDocs((d) => ({ ...d, [key]: data.url }));
      toast.success("Uploaded securely");
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploadingKey(""); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!docs.bank_statement_doc || !docs.gov_id_doc || !docs.selfie_doc) {
      toast.error("Please upload your bank statement, ID and a selfie for verification.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/seller/apply", { ...form, ...docs });
      await refresh();
      toast.success("Application submitted for review!");
      navigate("/seller");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setLoading(false);
  };

  if (user?.seller_status === "pending") return (
    <Layout><div className="max-w-lg mx-auto text-center py-24"><Clock className="w-14 h-14 mx-auto text-amber-500 mb-4" /><h1 className="font-display font-bold text-2xl">{t("applicationUnderReview")}</h1><p className="text-muted-foreground mt-2">{t("teamVerifying")}</p><p className="mt-3 inline-block rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold px-3 py-1.5" data-testid="sla-badge">⏱ Most stores are reviewed within 1–2 business days</p></div></Layout>
  );
  if (user?.seller_status === "approved") return (
    <Layout><div className="max-w-lg mx-auto text-center py-24"><CheckCircle2 className="w-14 h-14 mx-auto text-green-600 mb-4" /><h1 className="font-display font-bold text-2xl">{t("approvedSellerMsg")}</h1><Button className="mt-4 rounded-full" onClick={() => navigate("/seller")}>{t("goToSellerDashboard")}</Button></div></Layout>
  );

  return (
    <Layout hideAd>
      <div className="max-w-2xl mx-auto px-4 py-10">
        <div className="text-center mb-8"><Store className="w-12 h-12 mx-auto text-primary mb-3" /><h1 className="font-display font-bold text-3xl">{t("becomeXurraySeller")}</h1><p className="text-muted-foreground mt-2">{t("noSubscription")}</p></div>
        {user?.seller_status === "rejected" && (
          <div data-testid="reapply-reason" className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm">
            <p className="font-semibold text-red-700">Your previous application wasn't approved.</p>
            {user?.rejection_reason && <p className="text-red-600 mt-1">Reason: {user.rejection_reason}</p>}
            <p className="text-muted-foreground mt-1">We've pre-filled your details below — fix the issue, re-upload if needed, and resubmit.</p>
          </div>
        )}
        <Card className="p-6">
          <form onSubmit={submit} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              {[["business_name", t("businessName")], ["full_name", t("fullName")], ["phone", t("phoneLabel")], ["country", t("country")], ["tax_number", t("taxRegNumber")], ["mobile_money", t("mobileMoneyOptional")]].map(([k, l]) => (
                <div key={k}><Label className="text-xs">{l}</Label><Input data-testid={`seller-${k}`} required={k !== "mobile_money" && k !== "tax_number"} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1" /></div>
              ))}
            </div>
            <div><Label className="text-xs">{t("businessAddressField")}</Label><Textarea data-testid="seller-address" required value={form.business_address} onChange={(e) => setForm({ ...form, business_address: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-xs">{t("bankDetails")}</Label><Input data-testid="seller-bank" value={form.bank_details} onChange={(e) => setForm({ ...form, bank_details: e.target.value })} className="mt-1" /></div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Verification (required for approval)</Label>
              <p className="text-xs text-muted-foreground">Your bank statement and photos are stored privately and only visible to our admin team.</p>
              {[["bank_statement", "bank_statement_doc", "Bank statement", "image/*,.pdf", undefined],
                ["id_document", "gov_id_doc", "Government ID (passport / driver's licence)", "image/*,.pdf", undefined],
                ["selfie", "selfie_doc", "Photo verification (selfie)", "image/*", "user"]].map(([kind, key, l, accept, capture]) => (
                <label key={key} className="flex items-center gap-3 p-3 rounded-lg border border-dashed cursor-pointer hover:border-primary">
                  {kind === "selfie" ? <Camera className="w-4 h-4 text-muted-foreground" /> : <Upload className="w-4 h-4 text-muted-foreground" />}
                  <span className="text-sm flex-1">{l}</span>
                  {uploadingKey === key ? <Loader2 className="w-4 h-4 animate-spin text-primary" /> : docs[key] && <CheckCircle2 className="w-4 h-4 text-green-600" />}
                  <input type="file" data-testid={`upload-${key}`} accept={accept} capture={capture} onChange={uploadSecure(kind, key)} className="hidden" />
                </label>
              ))}
              <Label className="text-xs uppercase tracking-wider text-muted-foreground pt-2 block">Optional documents</Label>
              {[["business_cert_doc", t("businessCert")], ["export_license_doc", t("exportLicence")]].map(([k, l]) => (
                <label key={k} className="flex items-center gap-3 p-3 rounded-lg border border-dashed cursor-pointer hover:border-primary">
                  <Upload className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm flex-1">{l}</span>
                  {docs[k] && <CheckCircle2 className="w-4 h-4 text-green-600" />}
                  <input type="file" data-testid={`upload-${k}`} accept="image/*,.pdf" onChange={upload(k)} className="hidden" />
                </label>
              ))}
            </div>
            <Button data-testid="submit-seller-app" type="submit" disabled={loading} size="lg" className="w-full rounded-full">{loading ? "..." : t("submitApplication")}</Button>
          </form>
        </Card>
      </div>
    </Layout>
  );
}
